import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Injector,
  input,
  Signal,
} from '@angular/core';

import { CollapsibleCardAnalyticsDirective } from '@core/candidate-ui';
import { CandidateAnalytics } from '@core/candidate-util';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { Hotspots, IntegrationKinds } from '@core/integrations-types';
import { FeatureTooltipWrapperDirective } from '@core/product-updates-legacy';
import {
  ProductsPromoService,
  TellentHrIntegrationFeatureDiscovery,
  JourneysTellentHrIntegrationTooltip,
} from '@core/product-updates-logic';
import {
  DialogWidth,
  DotComponent,
  ButtonComponent,
  CollapsibleCardComponent,
  ConnectedOverlayDirective,
  DialogService,
  DropdownComponent,
  DropdownItemComponent,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  OverlayOriginDirective,
  PlaceloaderComponent,
} from '@core/user-interface-legacy';

import { ContextualIntegrationsApiService } from '../../../client/services/contextual-integrations-api.service';
import { CurrentHotspotToken } from '../../../client/services/current-hotspot.token';
import { IntegrationKindToken } from '../../../client/services/integration-kind.token';
import { PartnerToken } from '../../../client/services/partner.token';
import { DynamicHelpers } from '../../../dynamic/helpers';
import { SendCandidateDialogComponent } from '../../../dynamic/send-candidate-dialog/send-candidate-dialog.component';
import type { KiwiIntegrationsElement } from '../types';

@Component({
  selector: 'rti-kiwi-hr-integration-card',
  templateUrl: './kiwi-hr-integration-card.component.html',
  styleUrls: ['./kiwi-hr-integration-card.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CollapsibleCardComponent,
    IconComponent,
    HeadingComponent,
    TranslatePipe,
    IconButtonComponent,
    ConnectedOverlayDirective,
    OverlayOriginDirective,
    DropdownComponent,
    DropdownItemComponent,
    ButtonComponent,
    CollapsibleCardAnalyticsDirective,
    PlaceloaderComponent,
    DotComponent,
    FeatureTooltipWrapperDirective,
  ],
})
export class KiwiHrIntegrationCardComponent {
  private readonly dialogService = inject(DialogService);
  private readonly helpers = inject(DynamicHelpers);
  private readonly injector = inject(Injector);
  private readonly i18nService = inject(I18nService);
  private readonly candidateAnalytics = inject(CandidateAnalytics);

  private readonly tellentHrIntegrationFeatureDiscoveryService = inject(
    TellentHrIntegrationFeatureDiscovery,
  );
  private readonly journeysTellentHrIntegrationTooltip = inject(
    JourneysTellentHrIntegrationTooltip,
  );
  protected readonly productsPromoService = inject(ProductsPromoService);

  protected readonly tellentHrIntegrationFeatureDiscovery: Signal<
    TellentHrIntegrationFeatureDiscovery | JourneysTellentHrIntegrationTooltip
  > = computed(() =>
    this.productsPromoService.hasJourneys()
      ? this.journeysTellentHrIntegrationTooltip
      : this.tellentHrIntegrationFeatureDiscoveryService,
  );

  public integrationsElement = input.required<KiwiIntegrationsElement>();

  public isLoaded = input(false);

  public isProfileCreated = computed(
    () =>
      this.integrationsElement().integrationKind === IntegrationKinds.KIWI_HR &&
      this.integrationsElement().hotspot.name !== Hotspots.CandidateProfileMore,
  );

  public statusText = computed(() => {
    if (this.isProfileCreated()) {
      return this.i18nService.translate('rti.kiwi_hr.employee_profile_created');
    }

    const key = this.productsPromoService.hasJourneys()
      ? 'rti.kiwi_hr.journeys_create_profile'
      : 'rti.kiwi_hr.create_profile';

    return this.i18nService.translate(key);
  });

  public buttonText = computed(() => {
    if (this.isProfileCreated()) {
      return this.i18nService.translate('rti.kiwi_hr.view_in_kiwihr');
    }

    const key = this.productsPromoService.hasJourneys()
      ? 'rti.kiwi_hr.send_candidate.journeys_candidate_profile_more_button'
      : 'rti.kiwi_hr.send_candidate.candidate_profile_more_button';

    return this.i18nService.translate(key);
  });

  public ngOnDestroy() {
    if (this.tellentHrIntegrationFeatureDiscovery().isTriggered()) {
      this.tellentHrIntegrationFeatureDiscovery().onDismiss();
      this.tellentHrIntegrationFeatureDiscovery().isTriggered.set(false);
    }
  }

  public onMoreClick($event: MouseEvent, moreDropdown: ConnectedOverlayDirective): void {
    $event.stopImmediatePropagation();
    moreDropdown.toggleOverlay(!moreDropdown.open);
  }

  public onSendOrVisitClick(): void {
    if (this.isProfileCreated()) {
      this.onVisitKiwi();
    } else {
      this.onSendToKiwi();
    }
  }

  private onVisitKiwi(): void {
    const visitUrl = this.integrationsElement().inputs.url;

    if (!visitUrl) {
      return;
    }

    window.open(visitUrl, '_blank');
  }

  private onSendToKiwi(): void {
    const dialog = this.integrationsElement().inputs.dialog;
    const candidate = this.integrationsElement().inputs.candidate;

    if (!dialog || !candidate) {
      return;
    }

    this.candidateAnalytics.trackSendToKiwi();

    this.dialogService.openCustom(
      SendCandidateDialogComponent,
      {
        width: <DialogWidth>`${dialog.widthPx}px`,
        injector: Injector.create({
          parent: this.injector,
          providers: [
            { provide: CurrentHotspotToken, useValue: this.integrationsElement().hotspot },
            { provide: IntegrationKindToken, useValue: this.integrationsElement().integrationKind },
            { provide: PartnerToken, useValue: this.integrationsElement().partner },
            {
              provide: ContextualIntegrationsApiService,
              useClass: ContextualIntegrationsApiService,
            },
          ],
        }),
      },
      {
        title: this.helpers.buildText(dialog.title),
        subtitle: this.helpers.buildText(dialog.subtitle),
        formConfig: dialog.form,
        candidate: candidate,
      },
    );
  }
}
